import Vue from 'vue'
import firebase from 'firebase/app'
import 'firebase/database'
import 'firebase/functions'
import { functions } from '@/firebase'
import { normaliseDomain } from '@/utils/misc'
import router from '@/router'

export default {
  namespaced: true,
  state: {
    shops: {},
    shopRefs: {},
    nonce: null,
    linkingDomain: null,

    products: null,
    productQuery: null,
    productCursor: null,
    productHasNextPage: null,
    searchStore: null,
    selectedProductID: null,
    productImages: null,
    productImageCursor: null,
    imagesHasNextPage: null
  },
  getters: {
    shops (state) {
      return state.shops
    },
    shop (state) {
      return (domain) => {
        return state.shops[domain]
      }
    },
    shopRefs (state) {
      return state.shopRefs
    },
    nonce (state) {
      return state.nonce
    },
    linkingDomain (state) {
      return state.linkingDomain
    },
    shopInfo (state, getters, rootState, rootGetters) {
      const currentUser = rootGetters['auth/user']
      if (currentUser) return currentUser.shopify
      else return false
    },
    shopDomains (state, getters) {
      if (getters.shopInfo) {
        return Object.values(getters.shopInfo).map(store => {
          return store.shopDomain
        })
      } else return []
    },
    unpaidShopDomains (state, getters) {
      if (getters.shopDomains && getters.shops) {
        return getters.shopDomains.filter(domain => {
          return !(getters.shops[domain] && getters.shops[domain].sub && getters.shops[domain].sub.status === 'active')
        })
      } else return []
    },
    paidShops (state, getters) {
      if (getters.shopDomains && getters.shops) {
        return getters.shopDomains.map(domain => {
          return getters.shops[domain]
        }).filter(shop => {
          return !!(shop && shop.sub && shop.sub.status === 'active')
        })
      } else return []
    },
    hasUnpaidStore (state, getters) {
      if (getters.unpaidShops) return !!(getters.unpaidShops && getters.unpaidShops.length)
      else return false
    },
    isShopifyUser (state, getters) {
      return !!(getters.shopInfo)
    },
    isPaidShopifyUser (state, getters) {
      if (getters.paidShops) return !!(getters.paidShops && getters.paidShops.length)
      else return false
    },
    tokensExpired (state, getters) {
      if (getters.shopInfo) {
        const expiredDomains = []
        Object.values(getters.shopInfo).forEach(store => {
          if (store && store.tokenExpired) expiredDomains.push(store.shopDomain)
        })
        if (expiredDomains && expiredDomains.length) return expiredDomains
        else return false
      } else return false
    },
    products (state) {
      return state.products
    },
    productQuery (state) {
      return state.productQuery
    },
    searchStore (state) {
      return state.searchStore
    },
    productCursor (state) {
      return state.productCursor
    },
    moreProductsAvailable (state) {
      return state.productHasNextPage
    },
    selectedProductID (state) {
      return state.selectedProductID
    },
    productImages (state) {
      return state.productImages
    },
    productImageCursor (state) {
      return state.productImageCursor
    },
    moreImagesAvailable (state) {
      return state.imagesHasNextPage
    }
  },
  mutations: {
    setShop (state, { shopDomain, shop }) {
      if (shop) Vue.set(state.shops, shopDomain, shop)
      else Vue.delete(state.shops, shopDomain)
    },
    setShopRef (state, { shopDomain, shopRef }) {
      if (shopRef) Vue.set(state.shopRefs, shopDomain, shopRef)
      else Vue.delete(state.shopRefs, shopDomain)
    },
    setNonce (state, nonce) {
      state.nonce = nonce
    },
    setLinkingDomain (state, domain) {
      state.linkingDomain = domain
    },
    setProducts (state, { products, cursor, hasNextPage, append }) {
      if (append && state.products) {
        state.products = state.products.concat(products)
      } else {
        state.products = products
      }
      state.productCursor = cursor
      state.productHasNextPage = hasNextPage
    },
    setProductQuery (state, query) {
      state.productQuery = query
    },
    clearProducts (state) {
      state.products = null
      state.productCursor = null
      state.productHasNextPage = null
    },
    setProductImages (state, { images, cursor, hasNextPage, append }) {
      if (append && state.products) {
        state.productImages = state.productImages.concat(images)
      } else {
        state.productImages = images
      }
      state.productImageCursor = cursor
      state.imagesHasNextPage = hasNextPage
    },
    setSelectedProduct (state, id) {
      state.selectedProductID = id
    },
    setSearchStore (state, shopDomain) {
      state.searchStore = shopDomain
    },
    clearProductImages (state) {
      state.productImages = null
      state.productImageCursor = null
      state.imagesHasNextPage = null
    }
  },
  actions: {
    async loadShop ({ state, commit }, shopDomain) {
      if (state.shopRefs[shopDomain]) {
        // Make sure we only register one value listener at a time
        state.shopRefs[shopDomain].off()
      }
      const normalisedDomain = normaliseDomain(shopDomain)
      try {
        const shopRef = firebase.database().ref(`shopify_stores/${normalisedDomain}`)
        commit('setShopRef', { shopDomain, shopRef })

        shopRef.on('value', (snapshot) => {
          if (snapshot && snapshot.val()) {
            const shop = snapshot.val()
            commit('setShop', { shopDomain, shop: shop || false })
            if (shop && !(shop.sub && shop.sub.status === 'active') && !router.currentRoute.name.startsWith('shopify')) router.replace({ name: 'shopifySubscribe', params: { shopDomain } })
          } else commit('setShop', { shopDomain, shop: false })
        }, (error) => {
          console.log('Error listening to shopify store record: ', error)
          commit('setShop', { shopDomain, shop: false })
          commit('setShopRef', { shopDomain, shopRef: false })
        })
      } catch (error) {
        console.log('Unable to listen to store data events: ', normalisedDomain)
        commit('setShop', { shopDomain, shop: false })
        commit('setShopRef', { shopDomain, shopRef: false })
      }
    },
    async loadShops ({ getters, dispatch }) {
      if (getters.shopDomains) {
        return Promise.all(getters.shopDomains.map(shopDomain => {
          return dispatch('loadShop', shopDomain)
        }))
      }
    },
    async getOAuthLink (context, shopDomain) {
      const result = await functions.httpsCallable('shopify-getOAuthLink')({ shopDomain })
      if (result) return result.data
    },
    async getToken (context, query) {
      const result = await functions.httpsCallable('shopify-getToken')({ query })
      if (result) return result.data
    },
    async linkAccount ({ getters }) {
      const result = await functions.httpsCallable('shopify-linkAccount')({ nonce: getters.nonce })
      if (result) return result.data
    },
    async unlink ({ }, shopDomain) {
      const result = await functions.httpsCallable('shopify-unlinkAccount')({ shopDomain })
      if (result) return result.data
    },
    async subscribe (context, shopDomain) {
      return functions.httpsCallable('shopify-subscribe')({ shopDomain })
    },
    async checkSub (context, { shopDomain, subId }) {
      const result = await functions.httpsCallable('shopify-checkSub')({ shopDomain, subId })
      if (result) return result.data
    },
    async loadProducts ({ getters, commit }, { loadMore, query, shopDomain }) {
      const appending = loadMore && getters.moreProductsAvailable
      try {
        const result = await functions.httpsCallable('shopify-getProducts')({ cursor: appending ? getters.productCursor : null, query, shopDomain })
        if (result && result.data) {
          commit('setProducts', { products: result.data.products, cursor: result.data.cursor, hasNextPage: result.data.hasNextPage, append: appending })
        }
      } catch (err) {
        console.log('Error loading products: ', err.message)
      }
    },
    async loadProductImages ({ getters, commit }, { loadMore, productId, shopDomain }) {
      commit('setSelectedProduct', productId)
      const appending = loadMore && getters.moreImagesAvailable
      try {
        const result = await functions.httpsCallable('shopify-getProductImages')({ cursor: getters.productImageCursor, productId, shopDomain })
        if (result && result.data) {
          commit('setProductImages', { images: result.data.images, cursor: result.data.cursor, hasNextPage: result.data.hasNextPage, append: appending })
        }
      } catch (err) {
        console.log('Error loading product images: ', err.message)
      }
    }
  }
}
